import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";

import { api } from "../lib/chalkApi";
import { startCheckout } from "../lib/checkout";
import { DarkHeader, ChalkActionButton } from "../components/chalk/primitives";
import { usePaymentsOpen, EARLY_ACCESS_LABEL, EARLY_ACCESS_NOTE } from "../lib/billingStatus";

const fmt = (d) => d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });

function seasonEnd(sub) {
  let end = Date.now() + 120 * 86400000;
  if (sub?.status === "trialing" && sub.trial_ends_at) {
    end += Math.min(Math.max(new Date(sub.trial_ends_at) - Date.now(), 0), 30 * 86400000);
  }
  return new Date(end);
}

function Option({ id, title, price, note, badge, selected, onSelect }) {
  return (
    <button type="button" onClick={() => onSelect(id)} data-testid={`upgrade-option-${id}`}
      style={{
        width: "100%", textAlign: "left", marginTop: 10, padding: 14, borderRadius: 14, cursor: "pointer",
        background: selected ? "#F1F7F3" : "#fff",
        border: selected ? "1.5px solid var(--chalk-green, #2E7D52)" : "0.5px solid var(--chalk-border)",
        transition: "background-color .15s ease, border-color .15s ease",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--chalk-board)" }}>
          {title} {badge && <span style={{ fontSize: 10, marginLeft: 6, padding: "2px 8px", borderRadius: 999, background: "var(--chalk-amber-bg)", color: "#8a5a00" }}>{badge}</span>}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--chalk-board)" }}>{price}</div>
      </div>
      <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4, display: "flex", gap: 6, alignItems: "center" }}>
        {selected && <Check size={12} aria-hidden="true" />}{note}
      </div>
    </button>
  );
}

export default function Upgrade() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [pick, setPick] = useState("season");
  const [busy, setBusy] = useState(false);
  const open = usePaymentsOpen();
  useEffect(() => { api.mySubscription().then((d) => setSub(d.subscription)).catch(() => {}); }, []);

  const onSeason = sub?.plan === "season" && sub?.status === "active";
  const paidChalk = sub?.status === "active" && sub?.tier === "chalkboard" && !onSeason;
  const seasonEnds = onSeason ? new Date(sub.season_ends_at) : null;

  async function go() {
    setBusy(true);
    try {
      await startCheckout({ tier: "chalkboard", interval: pick, context: { source_screen: "upgrade_screen", feature_name: `chalkboard_${pick}` } });
    } catch (e) {
      if (e?.code === "STRIPE_NOT_CONFIGURED" || e?.code === "STRIPE_PRICE_MISSING") toast.info("Upgrades aren't available yet. Email support@getchalkos.com.");
      else toast.error(e.message);
      setBusy(false);
    }
  }

  return (
    <>
      <DarkHeader eyebrow="Upgrade" title="The Chalkboard" subtitle="Recurring practices, read receipts, more pins, volunteer signups." />
      <div style={{ padding: "0 18px 120px" }} data-testid="upgrade-screen">
        {paidChalk ? (
          <div data-testid="upgrade-already" style={{ marginTop: 16, fontSize: 14 }}>You're already on Chalkboard.
            <ChalkActionButton variant="ghost" label="Back" onClick={() => navigate(-1)} />
          </div>
        ) : (
          <>
            {onSeason && (
              <div data-testid="upgrade-season-active" style={{ marginTop: 14, fontSize: 13, color: "var(--chalk-board)" }}>
                Your Season runs through {fmt(seasonEnds)}. Switch to Monthly or Annual and your first charge waits until then.
              </div>
            )}
            {!onSeason && (
              <Option id="season" title="Season" price="$29" selected={pick === "season"} onSelect={setPick}
                note={`One time · 4 months · ends ${fmt(seasonEnd(sub))} · no auto-renew`} />
            )}
            <Option id="monthly" title="Monthly" price="$11.99/mo" selected={pick === "monthly"} onSelect={setPick}
              note={onSeason ? `First charge ${fmt(seasonEnds)}` : "Cancel any time"} />
            <Option id="annual" title="Annual" price="$119/yr" badge="Best value" selected={pick === "annual"} onSelect={setPick}
              note={onSeason ? `First charge ${fmt(seasonEnds)}` : "Two months free vs monthly"} />
            <div style={{ marginTop: 18 }}>
              <ChalkActionButton testId="upgrade-continue-btn" loading={busy}
                label={!open ? EARLY_ACCESS_LABEL : pick === "season" ? "Get Season for $29" : `Continue with ${pick === "annual" ? "Annual" : "Monthly"}`}
                onClick={go} disabled={!open || (onSeason && pick === "season")} />
            </div>
            {!open && <div data-testid="upgrade-early-access-note" style={{ fontSize: 13, color: "var(--chalk-board)", marginTop: 10 }}>{EARLY_ACCESS_NOTE}</div>}
            <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 10 }}>
              If a plan ends, your team keeps working on free Pickup and scheduled practices stay put.
            </div>
          </>
        )}
      </div>
    </>
  );
}
